import PageHeader from "@/components/PageHeader";
import { useToast } from "@/components/ui/use-toast";
import { PATH_ROOT, PATH_TEMPLATES, PRODUCT_PLACEHOLDER } from "@/constants";
import { ApiError } from "@/services/api";
import { createSurveyTemplate } from "@/services/survey-templates";
import { ReadSurvey, type WriteSurvey } from "@/types/survey";
import { useMutation } from "@tanstack/react-query";
import { exists } from "i18next";
import { NotepadTextDashed } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SurveyForm from "./SurveyForm";
import AddQuestionButton from "./components/Toolbar/components/AddQuestionButton";
import ClearButton from "./components/Toolbar/components/ClearButton";
import SaveButton from "./components/Toolbar/components/SaveButton";

const SurveyTemplateCreator = () => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const mutateSurveyTemplate = async (values: WriteSurvey) => {
    const response = await createSurveyTemplate(values);
    return {
      ...values,
      id: response.id,
    };
  };

  const mutation = useMutation<ReadSurvey, ApiError, WriteSurvey>({
    mutationFn: mutateSurveyTemplate,
    onSuccess: ({ id }) => {
      navigate(`${PATH_TEMPLATES}/${id}`);
      toast({
        title: t("surveyEditor.success.createSurveyTemplate"),
      });
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: t("surveyEditor.errors.createSurveyTemplate"),
        description,
        variant: "destructive",
      });
    },
  });

  const breadcrumbItems = useMemo(
    () => [
      { label: t("breadcrumb.home"), href: PATH_ROOT },
      { label: t("breadcrumb.surveyTemplates"), href: PATH_TEMPLATES },
      { label: t("breadcrumb.newSurveyTemplate") },
    ],
    [t],
  );

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <SurveyForm
        isNew
        onSubmit={mutation.mutate}
        busy={mutation.isPending}
        icon={<NotepadTextDashed className="w-6 h-6 mr-3" />}
        title={t("surveyEditor.surveyTemplateTitle")}
        buttons={
          <>
            <SaveButton />
            <AddQuestionButton />
            <ClearButton />
          </>
        }
        interQuestionToolbar={(questionIndex) => (
          <AddQuestionButton
            insertAt={questionIndex}
            label={t("surveyEditor.toolbar.insertQuestion")}
          />
        )}
        hint={t("surveyEditor.hintForTemplates", {
          product: PRODUCT_PLACEHOLDER,
        })}
      />
    </>
  );
};

export default SurveyTemplateCreator;
